import { widthToColumn } from "@client/lib/math";
import { getInherittedSettingValue } from "@client/lib/settings";
import {
  TargetSelector,
  combinedSettingsSelector,
  createShallowEqualSelector,
  entitiesSelector,
  makeEntitySelector,
  screenSelector
} from "@client/selectors";
import { createSelector } from "reselect";
import { localStateSelector } from "../selectors";
import { type OptInFormV2 } from "../types";

const makeLocalStateFieldsSelector = () => {
  return createSelector([localStateSelector], (localState) => localState.fields);
};

export const makeLocalStateFieldErrorsSelector = () => {
  return createSelector([localStateSelector], (localState) => localState.fieldErrors);
};

const makeLocalStateIsAllowedSelector = () => {
  return createSelector([localStateSelector], (localState) => localState.isAllowed);
};

const makeChildrenSelector = () => {
  const entitySelector = makeEntitySelector();
  const fieldsSelector = makeLocalStateFieldsSelector();

  return createShallowEqualSelector(
    [entitySelector, entitiesSelector, fieldsSelector, screenSelector],
    (entity, entities, fields, screen) => {
      const children: any[] = (entity.childrenIds || []).map((id) => entities[id]!);

      return children
        .map((entity) => ({
          entity,
          field: fields.find((f) => (entity.data as OptInFormV2.ModuleItemData).fieldId === f.id)
        }))
        .filter((p) => p.field)
        .map((x) => ({
          isRequired: getInherittedSettingValue(x.entity.settings, "", "required", screen),
          width: getInherittedSettingValue(x.entity.settings, "", "width", screen),

          field: x.field!
        }));
    }
  );
};

export const makeFieldsSelector = () => {
  const childrenSelector = makeChildrenSelector();
  const isAllowedSelector = makeLocalStateIsAllowedSelector();

  return createSelector([childrenSelector, isAllowedSelector], (children, isAllowed): OptInFormV2.FieldInfo[] => {
    return children.map((x) => ({
      ...x.field,
      isDisabled: !isAllowed,
      isRequired: x.isRequired,
      isVisible: true,
      width: widthToColumn(x.width || "100%")
    }));
  });
};

export const makeMessageSelector = () => {
  const settingsSelector = combinedSettingsSelector(TargetSelector.Entity, ["confirmation", "message"]);

  return createSelector([localStateSelector, settingsSelector], (localState, settings) => {
    if (localState.message) return localState.message;

    return settings.confirmation === "message" ? settings.message : null;
  });
};
